import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { api } from '@/App';
import FeedbackWidget from '@/components/FeedbackWidget';
import OnboardingModal from '@/components/OnboardingModal';
import WelcomeModal from '@/components/WelcomeModal';
import GuidedTour from '@/components/GuidedTour';
import BetaFeedbackButton from '@/components/BetaFeedbackButton';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { 
  ChartLine, Wallet, PiggyBank, TrendUp, Vault, 
  Bank, IdentificationCard, Coins, Calculator, 
  FileText, SignOut, List, X, Robot, Heart, Buildings, Money, ChartPieSlice, Bell, UsersThree, Path, ClockClockwise, Briefcase, Gauge, Compass, UserCircle, User, ClipboardText, Question
} from '@phosphor-icons/react';

const DashboardLayout = ({ user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cohort, setCohort] = useState(null);
  const [onboarding, setOnboarding] = useState({ open: false, steps: [] });
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    api.get('/cohort/info').then(res => setCohort(res.data)).catch(() => {});
    api.get('/onboarding/status').then(res => {
      const { completed, dismissed_at, steps } = res.data;
      if (!completed && !dismissed_at) {
        setOnboarding({ open: true, steps });
      } else {
        setOnboarding({ open: false, steps });
      }
    }).catch(() => {});
  }, []);

  const handleCompleteOnboarding = async () => {
    try { await api.post('/onboarding/complete'); } catch (e) {}
    track('onboarding_completed');
    setOnboarding((p) => ({ ...p, open: false }));
    toast.success('Welcome aboard. Open Getting Started anytime from the sidebar.');
  };

  const handleDismissOnboarding = async () => {
    try { await api.post('/onboarding/dismiss'); } catch (e) {}
    track('onboarding_dismissed');
    setOnboarding((p) => ({ ...p, open: false }));
  };

  // Sidebar order — reorganised Feb 2026 per beta feedback to match the
  // recommended setup journey: Profile → Income → Expenses → Net Worth →
  // Dashboard, then the rest of the analysis tools.
  const navigation = [
    // ── SETUP ──
    { name: 'Personal Profile', path: '/financial-profile', icon: User, group: 'setup' },
    { name: 'Income Sources', path: '/income-sources', icon: Money, group: 'setup' },
    { name: 'Spending Tracker', path: '/spending-tracker', icon: Wallet, group: 'setup' },
    { name: 'Net Worth', path: '/net-worth', icon: Buildings, group: 'setup' },
    { name: 'Dashboard', path: '/', icon: ChartLine, group: 'setup' },

    // ── ANALYSIS ──
    { name: 'Wealth Health Score', path: '/wealth-health', icon: Heart, group: 'analyze' },
    { name: 'Retirement Gap Analyzer', path: '/retirement-gap', icon: Gauge, group: 'analyze' },
    { name: 'Scenario Planner', path: '/scenario-planner', icon: Path, group: 'analyze' },
    { name: 'Journey Timeline', path: '/timeline', icon: ClockClockwise, group: 'analyze' },
    { name: 'Tax Optimizer', path: '/tax-optimizer', icon: Calculator, group: 'analyze' },
    { name: 'Social Security', path: '/social-security', icon: IdentificationCard, group: 'analyze' },
    { name: 'Roth Conversion', path: '/roth-conversion', icon: Coins, group: 'analyze' },
    { name: 'RMD Planning', path: '/rmd-planning', icon: Bank, group: 'analyze' },
    { name: 'Revenue Streams', path: '/revenue-streams', icon: TrendUp, group: 'analyze' },

    // ── PLANNING TOOLS ──
    { name: 'Budget Tracker', path: '/budget-tracker', icon: ChartPieSlice, group: 'tools' },
    { name: 'Savings Planner', path: '/savings-planner', icon: PiggyBank, group: 'tools' },
    { name: 'Inflation Simulator', path: '/inflation-simulator', icon: TrendUp, group: 'tools' },
    { name: 'Retirement Planner', path: '/retirement-planner', icon: Vault, group: 'tools' },

    // ── SUPPORT ──
    { name: 'AI Coach', path: '/ai-coach', icon: Robot, group: 'support' },
    { name: 'Household', path: '/household', icon: UsersThree, group: 'support' },
    { name: 'Advisor Dashboard', path: '/advisor', icon: Briefcase, group: 'support' },
    { name: 'Notifications', path: '/notifications', icon: Bell, group: 'support' },
    { name: 'Getting Started', path: '/getting-started', icon: Compass, group: 'support' },
    { name: 'Beta Evaluation', path: '/beta-evaluation', icon: ClipboardText, group: 'support' },
    { name: 'Reports', path: '/reports', icon: FileText, group: 'support' },
  ];

  const restartTour = async () => {
    try { await api.post('/beta-program/tour-status', { event: 'tour_restarted' }); } catch (_e) {}
    track('tour_restarted', { source: 'sidebar' });
    setTourOpen(true);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-surface-subtle flex">
      {/* Sidebar — enterprise fluent style: navy surface, gold left-accent for active */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-brand-navy transform transition-transform duration-300 ease-in-out lg:transform-none ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Brand mark */}
          <div className="px-6 py-6 border-b border-white/[0.06]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center">
                <span className="text-brand-gold font-bold text-sm">P</span>
              </div>
              <div>
                <h1 className="cabinet-grotesk text-[15px] font-medium text-white leading-tight">
                  PLANIVANCE <span className="text-brand-gold">AI</span>
                </h1>
                <p className="text-[10px] uppercase tracking-[0.14em] text-white/40 mt-0.5">Financial Intelligence</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
            {navigation.map((item, idx) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const prevGroup = idx > 0 ? navigation[idx - 1].group : null;
              const showGroupHeader = item.group && item.group !== prevGroup;
              const groupLabels = {
                setup: 'Setup Your Plan',
                analyze: 'Analyze & Optimize',
                tools: 'Planning Tools',
                support: 'Support & Resources',
              };
              return (
                <React.Fragment key={item.path}>
                  {showGroupHeader && (
                    <p className={`text-[10px] uppercase tracking-[0.14em] font-semibold text-brand-gold/70 ${idx === 0 ? 'mt-1' : 'mt-5'} mb-2 px-3`}>
                      {groupLabels[item.group]}
                    </p>
                  )}
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`relative flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-all duration-150 group ${
                      isActive
                        ? 'bg-white/[0.06] text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/[0.04]'
                    }`}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-sm bg-brand-gold" />
                    )}
                    <Icon size={17} weight={isActive ? 'fill' : 'regular'} className={isActive ? 'text-brand-gold' : 'text-white/60 group-hover:text-white/80'} />
                    <span className="tracking-[0.005em]">{item.name}</span>
                  </Link>
                </React.Fragment>
              );
            })}
          </nav>

          {/* User Section — refined enterprise footer */}
          <div className="px-3 pb-4 pt-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-3 px-3 py-2.5 mb-2 rounded-md bg-white/[0.03]">
              <div className="w-9 h-9 rounded-full bg-brand-gold/15 border border-brand-gold/25 flex items-center justify-center flex-shrink-0">
                <span className="text-brand-gold text-xs font-semibold">{(user.full_name || 'U').slice(0,2).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-white truncate">{user.full_name}</p>
                <p className="text-[11px] text-white/50 truncate">{user.email}</p>
              </div>
            </div>
            {cohort && cohort.beta_code_used && (
              <div className="inline-flex items-center gap-1.5 mb-2 mx-3 px-2 py-0.5 bg-brand-gold/12 rounded text-[10px] font-semibold uppercase tracking-[0.10em] text-brand-gold" data-testid="cohort-badge">
                <span className="w-1 h-1 bg-brand-gold rounded-full"></span>
                {cohort.cohort_label}
              </div>
            )}
            <Link
              to="/profile"
              data-testid="sidebar-profile-link"
              onClick={() => setSidebarOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-white/70 hover:text-white hover:bg-white/[0.04] rounded-md transition-colors"
            >
              <UserCircle size={16} />
              Profile &amp; Security
            </Link>
            <button
              onClick={restartTour}
              data-testid="sidebar-restart-tour-button"
              className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-white/70 hover:text-white hover:bg-white/[0.04] rounded-md transition-colors"
            >
              <Question size={16} />
              Restart Guided Tour
            </button>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-white/70 hover:text-white hover:bg-white/[0.04] rounded-md transition-colors"
            >
              <SignOut size={16} />
              Sign Out
            </button>
            <div className="mt-2 space-y-2">
              <BetaFeedbackButton variant="sidebar" testid="sidebar-beta-feedback-button" />
              <FeedbackWidget />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-30 px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="cabinet-grotesk text-lg font-medium text-[#0A1128]">
              PLANIVANCE AI
            </h1>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-button"
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              className="p-2 hover:bg-slate-100 rounded-md transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              {sidebarOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6 md:p-8 lg:p-12">
            <Outlet />
          </div>
        </main>

        <OnboardingModal
          open={onboarding.open}
          steps={onboarding.steps}
          onComplete={handleCompleteOnboarding}
          onDismiss={handleDismissOnboarding}
        />
        {/* PLANIVANCE AI Guided Tour (react-joyride) + first-login welcome */}
        <WelcomeModal onStartTour={() => setTourOpen(true)} />
        <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-xs text-[#4A5568] space-y-2">
              <p className="font-medium text-[#1A202C]">Important Disclaimers:</p>
              <p><strong>Educational Use:</strong> This tool is for educational purposes only and does not constitute financial advice.</p>
              <p><strong>Tax Information:</strong> Tax calculations are estimates. Consult a qualified tax professional for your specific situation.</p>
              <p><strong>Investment Disclaimer:</strong> Past performance does not guarantee future results. All investments carry risk.</p>
              <p><strong>Privacy:</strong> Your data is stored securely and never shared with third parties.</p>
              <p className="mt-3 text-[#0A1128] font-medium">PLANIVANCE AI - A Product of Outsource Solution Strategies, LLC</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DashboardLayout;